/* eslint-disable max-lines-per-function */
import type { UtilityContext } from '@change-corgi/core/react/utilityContext';

import { isDecisionMaker } from './decisionMaker';
import { fetchGammaPetitionDetailsExperiments } from './experiments';
import type { GammaPetitionDetailsDataExperimentsQuery } from './experiments.graphql';
import { isGdprConsentProvided } from './gdprConsentProvided';
import { normalizePlatypusPetitionSignGooglePrefillExperiment } from './normalize/normalizePlatypusPetitionSignGooglePrefillExperiment';
import type { PetitionViewerInfo } from './petitionViewerInfo';
import { getPetitionViewerInfo } from './petitionViewerInfo';

export type PetitionDetailsPageUserData = PetitionViewerInfo & {
	userId: string | undefined;
	decisionMaker: boolean;
	gdprConsentProvided: boolean;
	signatureThermometerEnabled: boolean;
	isSunriseEnabled: boolean;
	shareVisualsExperimentEnabled: boolean;
	isMobileAppDownloadBannerEnabled: boolean;
	fullSunrisePetitionPageShareComponentExperiment: boolean;
	fullSunrisePetitionHeaderExperiment: 'control' | 'fake_control' | 'variant_a' | 'variant_b';
	fullSunriseActionPanelExperimentVariation: string;
	platypusPetitionSignGooglePrefillExperiment: 'control' | 'fake_control' | 'variant1';
	petitionPageMomentumBarExperiment: 'control' | 'enabled';
	experiments: Omit<
		GammaPetitionDetailsDataExperimentsQuery['experiments'],
		| 'sunriseSupportersV1'
		| 'supporterShareVisualsExperiment'
		| 'signatureThermometerExperimentDesktop'
		| 'fullSunrisePetitionPageShareComponentExperiment'
		| 'fullSunrisePetitionHeaderExperiment'
		| 'actionPanelSunriseExperiment'
		| 'mobileAppDownloadBanner'
		| 'platypusPetitionSignGooglePrefillExperiment'
		| 'petitionPageMomentumBarExperiment'
	>;
};

export async function getPetitionDetailsPageUserData(
	utilityContext: UtilityContext,
	slugOrId: string,
	{
		currentUserId,
		isGuest,
		decisionMakerHash,
	}: {
		currentUserId: string | undefined;
		isGuest: boolean;
		decisionMakerHash?: string;
		hasShowLegislativeBodiesParam: boolean;
	},
): Promise<PetitionDetailsPageUserData> {
	const [petitionViewerInfo, decisionMaker, gdprConsentProvided, experiments] = await Promise.all([
		getPetitionViewerInfo(utilityContext, slugOrId, currentUserId),
		!isGuest || decisionMakerHash
			? isDecisionMaker(utilityContext, { slugOrId, decisionMakerHash })
			: Promise.resolve(false),
		isGdprConsentProvided(utilityContext),
		// FIXME remove 'Gamma' from function and operation name
		fetchGammaPetitionDetailsExperiments(utilityContext),
	]);

	const {
		sunriseSupportersV1,
		supporterShareVisualsExperiment,
		signatureThermometerExperimentDesktop,
		fullSunrisePetitionPageShareComponentExperiment,
		fullSunrisePetitionHeaderExperiment,
		actionPanelSunriseExperiment,
		mobileAppDownloadBanner,
		platypusPetitionSignGooglePrefillExperiment: platypusPetitionSignGooglePrefillExperimentRaw,
		petitionPageMomentumBarExperiment,
		...experimentsRest
	} = experiments;

	const platypusPetitionSignGooglePrefillExperiment = await normalizePlatypusPetitionSignGooglePrefillExperiment({
		experiment: platypusPetitionSignGooglePrefillExperimentRaw,
		utilityContext,
	});

	return {
		...petitionViewerInfo,
		userId: currentUserId,
		decisionMaker,
		gdprConsentProvided,
		signatureThermometerEnabled: signatureThermometerExperimentDesktop.variation === 'v1',
		isSunriseEnabled: sunriseSupportersV1.variation === 'enabled',
		shareVisualsExperimentEnabled: supporterShareVisualsExperiment.variation === 'v1',
		isMobileAppDownloadBannerEnabled: mobileAppDownloadBanner.variation === 'enabled',
		fullSunrisePetitionPageShareComponentExperiment:
			fullSunrisePetitionPageShareComponentExperiment.variation === 'enabled',
		fullSunrisePetitionHeaderExperiment: fullSunrisePetitionHeaderExperiment.variation,
		fullSunriseActionPanelExperimentVariation: actionPanelSunriseExperiment.variation,
		platypusPetitionSignGooglePrefillExperiment,
		petitionPageMomentumBarExperiment: petitionPageMomentumBarExperiment.variation,
		experiments: experimentsRest,
	};
}
